import { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  Phone, Star, MapPin, CheckCircle2, XCircle, ArrowRight, Clock, Store, Flag,
} from "lucide-react";
import { Header } from "@/components/landing/Header";
import { Footer } from "@/components/landing/Footer";
import { MobileBar } from "@/components/landing/MobileBar";
import { Booking } from "@/components/landing/Booking";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/landing/Reveal";
import { SEO_PAGES, SERVICE_LIST, PHONE, PHONE_TEL, scrollToId, BUSINESS_REF } from "@/lib/site";

const DEFAULT_BULLETS = [
  "Free pickup & delivery at your door",
  "Back fresh in ~48 hours",
  "Veteran & family-owned",
];

const ZipCheck = ({ zips, city }) => {
  const [zip, setZip] = useState("");
  const [result, setResult] = useState(null);
  const check = (e) => {
    e.preventDefault();
    setResult(zips.includes(zip.trim()));
  };
  return (
    <div className="mt-6">
      <form onSubmit={check} className="flex max-w-md gap-2">
        <input
          data-testid="city-zip-input"
          value={zip}
          onChange={(e) => setZip(e.target.value.replace(/\D/g, "").slice(0, 5))}
          placeholder={`Your ${city} zip code`}
          inputMode="numeric"
          className="w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm font-semibold text-brand-navy placeholder:text-slate-400 focus:border-brand-blue focus:outline-none"
        />
        <button
          data-testid="city-zip-button"
          type="submit"
          className="shrink-0 rounded-xl bg-brand-navy px-5 py-3 text-sm font-bold text-white transition-colors hover:bg-brand-navy-light"
        >
          Check
        </button>
      </form>
      {result !== null && (
        <p
          data-testid="city-zip-result"
          className={`mt-3 flex items-center gap-2 text-sm font-semibold ${result ? "text-emerald-600" : "text-rose-500"}`}
        >
          {result ? (
            <><CheckCircle2 className="h-4 w-4" /> Yes — free pickup &amp; delivery in {city}!</>
          ) : (
            <><XCircle className="h-4 w-4" /> Not on a {city} route — call {PHONE} and we&apos;ll try to help.</>
          )}
        </p>
      )}
    </div>
  );
};

const FaqJsonLd = ({ faq }) => (
  <script
    type="application/ld+json"
    dangerouslySetInnerHTML={{
      __html: JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: faq.map((f) => ({
          "@type": "Question",
          name: f.q,
          acceptedAnswer: { "@type": "Answer", text: f.a },
        })),
      }),
    }}
  />
);

export default function SeoPage({ page }) {
  const navigate = useNavigate();
  const full = Boolean(page.faq);
  const otherCities = SEO_PAGES.filter((p) => p.kind === "city" && p.path !== page.path);
  const wedding = SEO_PAGES.find((p) => p.kind === "service");

  return (
    <div className="bg-slate-50 text-slate-900">
      <Seo title={page.title} description={page.meta} path={page.path} jsonLd={BUSINESS_REF} />
      {page.faq && <FaqJsonLd faq={page.faq} />}
      <Header />
      <main>
        <section className="grain relative overflow-hidden bg-brand-navy pb-20 pt-44 text-white">
          <div className="pointer-events-none absolute inset-0 bg-[radial-gradient(circle_at_80%_10%,rgba(37,99,235,0.2),transparent_55%),radial-gradient(circle_at_10%_90%,rgba(245,158,11,0.08),transparent_50%)]" />
          <div className="relative z-10 mx-auto max-w-4xl px-4 sm:px-6">
            <Reveal>
              <p className="inline-flex items-center gap-2 rounded-full border border-amber-300/30 bg-white/5 px-4 py-1.5 text-xs font-bold uppercase tracking-[0.2em] text-amber-200 backdrop-blur">
                <Star className="h-3.5 w-3.5 fill-fall-amber text-fall-amber" />
                Oxford Cleaners · Veteran &amp; Family-Owned
              </p>
              <h1 className="mt-5 font-display text-3xl font-extrabold leading-tight tracking-tight sm:text-4xl lg:text-5xl">
                {page.h1}
              </h1>
              <p className="mt-5 max-w-2xl text-base leading-relaxed text-slate-300">{page.intro}</p>
              {page.homeLink && (
                <p className="mt-3 max-w-2xl text-base leading-relaxed text-slate-300">
                  Come by{" "}
                  <Link to="/" className="font-bold text-fall-amber underline decoration-2 underline-offset-4 hover:text-amber-300">
                    our Chandler dry cleaning shop
                  </Link>{" "}
                  — or skip the trip and let us come to you.
                </p>
              )}
              {!full && (
                <ul className="mt-7 space-y-2.5">
                  {DEFAULT_BULLETS.map((b) => (
                    <li key={b} className="flex items-center gap-2.5 text-sm font-semibold text-slate-200">
                      <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-400" /> {b}
                    </li>
                  ))}
                </ul>
              )}
              <div className="mt-9 flex flex-wrap gap-3">
                <button
                  data-testid="seo-page-book-button"
                  onClick={() => (full ? scrollToId("book") : navigate("/", { state: { scrollTo: "book" } }))}
                  className="rounded-full bg-fall-amber px-7 py-3.5 font-display text-sm font-extrabold uppercase tracking-widest text-brand-navy shadow-[0_0_25px_rgba(245,158,11,0.4)] transition-transform hover:scale-105"
                >
                  {page.bookCta || "Book free pickup"}
                </button>
                <a
                  data-testid="seo-page-call-link"
                  href={PHONE_TEL}
                  className="flex items-center gap-2 rounded-full border border-white/25 px-7 py-3.5 text-sm font-bold transition-colors hover:border-fall-amber hover:text-fall-amber"
                >
                  <Phone className="h-4 w-4" /> {PHONE}
                </a>
              </div>
            </Reveal>
          </div>
        </section>

        {page.neighbor && (
          <section className="bg-white py-20">
            <div className="mx-auto grid max-w-4xl items-center gap-10 px-4 sm:grid-cols-[auto_1fr] sm:px-6">
              <Reveal>
                <img
                  data-testid="city-owner-photo"
                  src="/images/owner_military.jpg"
                  alt="Wais Kushkaki, Oxford Cleaners owner, in U.S. Army dress uniform"
                  className="mx-auto h-52 w-44 rounded-2xl border-4 border-fall-amber object-cover object-top shadow-xl"
                />
              </Reveal>
              <Reveal delay={0.1}>
                <h2 className="font-display text-xl font-bold tracking-tight text-brand-navy sm:text-2xl">{page.neighbor.h2}</h2>
                <p className="mt-4 text-base leading-relaxed text-slate-600">{page.neighbor.text}</p>
              </Reveal>
            </div>
          </section>
        )}

        {full && (
          <section className={`py-20 ${page.neighbor ? "bg-slate-50" : "bg-white"}`}>
            <div className="mx-auto max-w-4xl px-4 sm:px-6">
              <Reveal>
                <h2 className="font-display text-xl font-bold tracking-tight text-brand-navy sm:text-2xl">
                  What we pick up in {page.city}
                </h2>
                <ul className="mt-6 grid gap-3 sm:grid-cols-2">
                  {SERVICE_LIST.map((s) => (
                    <li key={s} className="flex items-start gap-2.5 rounded-2xl border border-slate-200 bg-white p-4 text-sm font-semibold text-slate-700">
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-brand-blue" /> {s}
                    </li>
                  ))}
                </ul>
              </Reveal>
            </div>
          </section>
        )}

        {full && (
          <section className="bg-brand-navy py-20 text-white">
            <div className="mx-auto max-w-4xl px-4 sm:px-6">
              <Reveal>
                <h2 className="font-display text-xl font-bold tracking-tight sm:text-2xl">How it works</h2>
                <ol className="mt-8 grid gap-5 sm:grid-cols-3">
                  {page.howItWorks.map((step, i) => (
                    <li key={i} className="rounded-3xl border border-white/10 bg-white/5 p-6 backdrop-blur">
                      <span className="text-outline font-display text-4xl font-extrabold">0{i + 1}</span>
                      <p className="mt-3 text-sm leading-relaxed text-slate-200">{step}</p>
                    </li>
                  ))}
                </ol>
              </Reveal>
            </div>
          </section>
        )}

        {full && (
          <section className="bg-white py-20">
            <div className="mx-auto max-w-4xl px-4 sm:px-6">
              <Reveal>
                <h2 className="font-display text-xl font-bold tracking-tight text-brand-navy sm:text-2xl">
                  {page.neighborhoodsH2}
                </h2>
                <div className="mt-5 flex flex-wrap gap-2">
                  {page.zips.map((z) => (
                    <span key={z} className="flex items-center gap-1.5 rounded-full border border-brand-blue/25 bg-brand-blue-light px-3.5 py-1.5 text-sm font-bold text-brand-navy">
                      <MapPin className="h-3.5 w-3.5 text-fall-orange" /> {z}
                    </span>
                  ))}
                </div>
                <p className="mt-5 text-base leading-relaxed text-slate-600">
                  Including {page.neighborhoods}.
                </p>
                <ZipCheck zips={page.zips} city={page.city} />
              </Reveal>
            </div>
          </section>
        )}

        {page.whyChoose && (
          <section className="bg-slate-50 py-20">
            <div className="mx-auto max-w-4xl px-4 sm:px-6">
              <Reveal>
                <h2 className="font-display text-xl font-bold tracking-tight text-brand-navy sm:text-2xl">
                  Why {page.city} chooses Oxford
                </h2>
                <ul className="mt-6 grid gap-3 sm:grid-cols-2">
                  {page.whyChoose.map((w) => (
                    <li key={w} className="flex items-start gap-2.5 rounded-2xl border border-slate-200 bg-white p-4 text-sm font-semibold text-slate-700">
                      <Star className="mt-0.5 h-4 w-4 shrink-0 fill-fall-amber text-fall-amber" /> {w}
                    </li>
                  ))}
                </ul>
              </Reveal>
            </div>
          </section>
        )}

        {page.veteran && (
          <section className="bg-brand-navy py-16 text-white">
            <div className="mx-auto flex max-w-4xl items-start gap-4 px-4 sm:px-6">
              <Flag className="mt-1 h-8 w-8 shrink-0 text-fall-amber" />
              <Reveal>
                <h2 className="font-display text-xl font-bold tracking-tight sm:text-2xl">{page.veteran.h2}</h2>
                <p className="mt-3 text-base leading-relaxed text-slate-300">{page.veteran.text}</p>
              </Reveal>
            </div>
          </section>
        )}

        {page.visitShop && (
          <section className="bg-white py-20">
            <div className="mx-auto grid max-w-4xl items-center gap-10 px-4 sm:px-6 lg:grid-cols-2">
              <Reveal>
                <h2 className="font-display text-xl font-bold tracking-tight text-brand-navy sm:text-2xl">Visit the shop</h2>
                <p className="mt-4 flex items-start gap-2.5 text-base font-semibold text-slate-700">
                  <Store className="mt-0.5 h-5 w-5 shrink-0 text-fall-orange" />
                  5865 W Ray Rd, Suite 3, Chandler, AZ 85226
                </p>
                <p className="mt-3 flex items-start gap-2.5 text-base font-semibold text-slate-700">
                  <Clock className="mt-0.5 h-5 w-5 shrink-0 text-fall-orange" />
                  Mon–Fri 7am–5pm · Sat 10am–2pm · Sun closed
                </p>
                <p className="mt-4 text-base leading-relaxed text-slate-600">
                  Drop-offs are welcome at the counter — or skip the trip with free pickup &amp; delivery.
                </p>
              </Reveal>
              <Reveal delay={0.1}>
                <iframe
                  data-testid="chandler-map-embed"
                  title="Oxford Cleaners on Google Maps"
                  src="https://www.google.com/maps?q=Oxford%20Cleaners%205865%20W%20Ray%20Rd%20Suite%203%20Chandler%20AZ%2085226&output=embed"
                  className="h-72 w-full rounded-3xl border border-slate-200 shadow-sm"
                  loading="lazy"
                />
              </Reveal>
            </div>
          </section>
        )}

        {page.faq && (
          <section className={`py-20 ${page.visitShop ? "bg-slate-50" : "bg-white"}`}>
            <div className="mx-auto max-w-4xl px-4 sm:px-6">
              <Reveal>
                <h2 className="font-display text-xl font-bold tracking-tight text-brand-navy sm:text-2xl">
                  {page.city} FAQ
                </h2>
                <div className="mt-6 space-y-4">
                  {page.faq.map((f) => (
                    <div key={f.q} className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
                      <h3 className="font-display text-base font-bold text-brand-navy">{f.q}</h3>
                      <p className="mt-2 text-sm leading-relaxed text-slate-600">{f.a}</p>
                    </div>
                  ))}
                </div>
              </Reveal>
            </div>
          </section>
        )}

        {full && <Booking presetCity={page.city} />}

        <section className="bg-white py-16">
          <div className="mx-auto max-w-4xl px-4 sm:px-6">
            <Reveal>
              <h2 className="font-display text-lg font-bold text-brand-navy sm:text-xl">
                More from Oxford Cleaners
              </h2>
              <div className="mt-5 grid gap-3 sm:grid-cols-2">
                {otherCities.map((c) => (
                  <Link
                    key={c.path}
                    to={c.path}
                    className="group flex items-center justify-between rounded-2xl border border-slate-200 bg-slate-50 px-5 py-4 text-sm font-bold text-brand-navy transition-colors hover:border-brand-blue hover:bg-brand-blue-light"
                  >
                    {c.city} dry cleaning
                    <ArrowRight className="h-4 w-4 text-brand-blue transition-transform group-hover:translate-x-1" />
                  </Link>
                ))}
                {page.kind !== "service" && wedding && (
                  <Link
                    to={wedding.path}
                    className="group flex items-center justify-between rounded-2xl border border-slate-200 bg-slate-50 px-5 py-4 text-sm font-bold text-brand-navy transition-colors hover:border-brand-blue hover:bg-brand-blue-light"
                  >
                    Wedding dress cleaning &amp; preservation
                    <ArrowRight className="h-4 w-4 text-brand-blue transition-transform group-hover:translate-x-1" />
                  </Link>
                )}
              </div>
              <p className="mt-8 flex items-center gap-2 text-sm font-semibold text-slate-500">
                <Clock className="h-4 w-4 text-fall-orange" />
                Open Mon–Fri 7am–5pm · Sat 10am–2pm · 5865 W Ray Rd, Suite 3, Chandler, AZ 85226
              </p>
            </Reveal>
          </div>
        </section>
      </main>
      <Footer />
      <MobileBar />
    </div>
  );
}
